// Pure helpers backing the reserve-fund widget's ring-fence guard.
//
// Owned by task 3.5 of the dashboard-redesign spec.
// See:
//   - `.kiro/specs/dashboard-redesign/design.md` §Correctness Properties · P5
//   - `.kiro/specs/dashboard-redesign/requirements.md` Requirement 7.2
//   - `.kiro/steering/constitution.md` Rule 2 (GL — reserve funds are a
//     sub-ledger reconciling to the Reserve control account, ring-fenced
//     per building and never commingled)
//
// The `useReserveFundPosition` hook returns the full merged position
// (balance + 12-month movements + fixed copy string). What it deliberately
// does NOT do is decide **whether** the ring-fencing note should be shown
// on the widget — that gating rule lives here, in a pure helper, so Wave 4
// (`Widget_ReserveFundPosition`, task 4.3) can consume it verbatim and the
// P5 property test can exercise the invariant without touching a React
// render tree.
//
// Rule 2 legal/trust-fund context: aggregating reserve funds across
// buildings is INFORMATIONAL only — the sum is never a spendable pool.
// When the dashboard is in aggregate scope and two or more buildings
// contribute to the aggregated balance, the widget MUST render the
// ring-fencing note. When the aggregation is a single building (or the
// balance is zero) the note is meaningless and MUST NOT appear.

/**
 * Structural subset of `ReserveFundPosition` consumed by the ring-fence
 * gate. Defined here rather than imported from `./types` so this module
 * stays a leaf helper — no React, no side effects, no cross-module cycles.
 * `ReserveFundPosition` (see `./types.ts`) is compatible with this shape by
 * structural subtyping, so widget code can pass its `WidgetResult` data
 * payload directly.
 */
export interface ReserveFundBalance {
  /** Aggregated reserve balance across `aggregatedFromBuildings`, in pence. */
  balancePence: number;
  /**
   * Building IDs contributing to `balancePence`. Length ≥ 2 signals an
   * aggregate view spanning multiple buildings — the exact scenario
   * Requirement 7.2 targets. In single-building mode this array has length
   * exactly 1 (or 0 when the tenant has no buildings yet).
   */
  aggregatedFromBuildings: readonly string[];
}

/**
 * Whether the reserve-fund widget must display its ring-fencing note.
 *
 * Returns `true` iff BOTH conditions hold:
 *   - `aggregatedFromBuildings.length >= 2` — the widget is showing an
 *     aggregate across multiple buildings' ring-fenced funds; and
 *   - `balancePence > 0` — there's a non-zero aggregate to caveat. A £0.00
 *     aggregate would render the note pointlessly and, worse, add visual
 *     noise to the day-1 empty-state widget covered by Requirement 12.
 *
 * The helper is intentionally scope-agnostic: `aggregatedFromBuildings.length`
 * is the ground-truth signal for "aggregate mode" here — in single-building
 * mode the backend returns exactly one contributing building, so the length
 * check implicitly gates on `scope.kind === 'all'` without this helper
 * having to know about `BuildingScope`.
 */
export function shouldShowRingFenceNote(
  balance: ReserveFundBalance,
): boolean {
  if (!Number.isFinite(balance.balancePence)) return false;
  if (balance.balancePence <= 0) return false;
  return balance.aggregatedFromBuildings.length >= 2;
}
